import { rem } from 'polished'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AlwaysOnPackageSelectionModal from 'components/OfferPage/AlwaysOn/MobileBookingFlow/Modals/AlwaysOnPackageSelectionModal'
import AlwaysOnRoomAndPackageDetailsModal from 'components/OfferPage/AlwaysOn/MobileBookingFlow/Modals/AlwaysOnRoomAndPackageDetailsModal'
import AlwaysOnRoomSelectionModal from 'components/OfferPage/AlwaysOn/MobileBookingFlow/Modals/AlwaysOnRoomSelectionModal'
import OfferAirportOptions from 'components/OfferPage/Filters/Airport/OfferAirportOptions'
import OfferAirportSelectBox from 'components/OfferPage/Filters/Airport/OfferAirportSelectBox'
import OfferCalendarDatePickerSelectBox from 'components/OfferPage/Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCalendarSidebarDatePicker from 'components/OfferPage/Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import OfferCapacityWithAgesSelectBox from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelection from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferDurationOptions from 'components/OfferPage/Filters/Duration/OfferDurationOptions'
import { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import OfferSidebarDatePicker from 'components/OfferPage/Filters/SidebarDatePicker/OfferSidebarDatePicker'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import config from 'constants/config'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { min } from 'lib/array/arrayUtils'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.default.five};
    }
  }
`

interface Props {
  className?: string
  onViewPackageOptions?: () => void
  activeFilter: OfferPageFilterType
  capacities?: Array<App.RoomCapacity>
  durationOptions: Array<OfferDurationOption>
  durationOptionsOverride?: Array<OfferDurationOption>
  shouldShowCalendarWithoutPricing?: boolean
  filterHistory: Array<OfferPageFilterType>
}

function PropertySidebarFilters(props: Props) {
  const {
    onViewPackageOptions,
    activeFilter,
    capacities,
    durationOptions,
    shouldShowCalendarWithoutPricing,
    filterHistory,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)
  const hasPreviousFilter = useMemo(() => filterHistory.length > 0, [filterHistory])

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterBack })
    }
  }, [dispatch])

  const onApply = useCallback(() => {
    filterContinue()
    onViewPackageOptions?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [filterContinue, onViewPackageOptions, onUserAnalyticsAction])

  const onBackHandler = useMemo(() => (
    hasPreviousFilter ? onBack : undefined
  ), [hasPreviousFilter, onBack])

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  const durationSubtitle = minimumDuration > 1
    ? intl.formatMessage(
        {
          id: 'tjSS+qTvfW',
          defaultMessage: 'Minimum stay for this offer is {count, plural, one {# night} other {# nights}}',
          description: 'Subtitle for duration filter showing minimum stay requirement',
        },
        { count: minimumDuration },
      )
    : undefined

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  return (
    <ModalBase
      isOpen={delayedOpen}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={`Your ${config.brandConfig.holidaySynonym}`}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              <OfferCapacityWithAgesSelectBox inSidebar />
              <OfferCalendarDatePickerSelectBox inSidebar />
              <OfferAirportSelectBox inSidebar />
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'eBVUuVU8c/',
              defaultMessage: 'Where will you depart from?',
              description: 'Modal title for airport selection filter',
            })}
            subtitle={intl.formatMessage({
              id: 'ga0iScZSuQ',
              defaultMessage: 'Select your preferred airport',
              description: 'Modal subtitle for airport selection filter',
            })}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 't/ZbBg0HFO',
                defaultMessage: 'Continue',
                description: 'Button text to continue from calendar picker',
              }),
              onClick: onApply,
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'x/8HRczMD+',
              defaultMessage: 'Who is going?',
              description: 'Modal title for capacity selection filter',
            })}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 'rmnd5V9w3G',
                defaultMessage: 'Apply',
                description: 'Button text to apply capacity filter',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && shouldShowCalendarWithoutPricing && <OfferSidebarDatePicker
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'checkin' && !shouldShowCalendarWithoutPricing && <OfferCalendarSidebarDatePicker
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'duration' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'hXF2p9oBtq',
              defaultMessage: 'How long do you want to stay?',
              description: 'Modal title for duration selection filter',
            })}
            subtitle={durationSubtitle}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <ModalContent>
              <OfferDurationOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 't/ZbBg0HFO',
                defaultMessage: 'Continue',
                description: 'Button text to continue from calendar picker',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'roomSelection' && <AlwaysOnRoomSelectionModal
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'packageSelection' && <AlwaysOnPackageSelectionModal
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'roomDetails' && <AlwaysOnRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  activeFilter: state.activeFilter,
  durationOptions: ownProps.durationOptionsOverride ?? getDurationOptions(state, appState),
  filterHistory: state.filterHistory,
}), PropertySidebarFilters)
